<template>
  <div>
    <v-menu
      ref="datePickerMenu"
      v-model="menu"
      v-model:return-value="localValue"
      min-width="285px"
      max-width="285px"
      transition="scale-transition"
      :close-on-content-click="false"
    >
      <template #activator="{ props }">
        <AGTextField
          :id="id"
          append-icon="mdi-calendar-outline"
          autocomplete="off"
          class="datepicker-input"
          density="compact"
          hide-details="auto"
          clearable
          readonly
          :hint="hint"
          :model-value="dateRangeText"
          :disabled="disabled"
          :label="label"
          :placeholder="placeholder"
          :data-message-color="messageColor"
          :rules="rules"
          v-bind="props"
          @click:clear="onClear"
        />
      </template>

      <div class="ag-month-picker">
        <div class="ag-month-picker__header">
          <AGButtonIcon icon="md:chevron_left" size="md" @click="currentYear--" />
          <div class="ag-month-picker__value">
            {{ currentYear }}
          </div>
          <AGButtonIcon icon="md:chevron_right" size="md" @click="currentYear++" />
        </div>
        <div class="ag-month-picker__body">
          <button
            v-for="month in months"
            :key="month.value"
            :data-selected="isSelected(month)"
            @click="onSelectMonth(month)"
          >
            {{ month.label }}
          </button>
        </div>
        <div class="ag-date-picker-range-month__actions">
          <v-spacer />
          <AGButton type="secondary" @click="onCancel()"> Cancelar </AGButton>
          <AGButton type="primary" @click="onConfirm()"> Confirmar </AGButton>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGButtonIcon from '@/components/common/AGButtonIcon.vue';
import AGTextField from '@/components/common/AGTextField.vue';
import { MESSAGE_COLOR } from '@/constants/ConstantMessageColor';
import UtilsDate from '@/utils/UtilsDate';
import { cloneDeep } from 'lodash';

export default {
  components: { AGButtonIcon, AGTextField, AGButton },

  emits: ['update:modelValue', 'change'],

  props: {
    modelValue: {
      type: Array,
      default: () => {
        return [];
      },
    },

    id: {
      type: String,
      required: true,
    },

    label: {
      type: String,
      require: true,
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    type: {
      type: String,
      default: 'month',
    },

    hint: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: 'Selecione uma ou mais datas',
    },

    messageColor: {
      type: String,
      default: MESSAGE_COLOR.DEFAULT,
      validator: (val) => Object.values(MESSAGE_COLOR).includes(val),
    },

    rules: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      menu: false,
      currentYear: new Date().getFullYear(),
      selectedMonth: cloneDeep(this.modelValue),
      months: [
        { label: 'jan.', value: '01' },
        { label: 'fev.', value: '02' },
        { label: 'mar.', value: '03' },
        { label: 'abr.', value: '04' },
        { label: 'mai.', value: '05' },
        { label: 'jun.', value: '06' },
        { label: 'jul.', value: '07' },
        { label: 'ago.', value: '08' },
        { label: 'set.', value: '09' },
        { label: 'out.', value: '10' },
        { label: 'nov.', value: '11' },
        { label: 'dez.', value: '12' },
      ],
    };
  },

  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },

      set(value) {
        this.selectedMonth = value;
        this.$emit('update:modelValue', value);
      },
    },
    dateRangeText() {
      return this.modelValue.map((item) => UtilsDate.format(item, 'MM/Y', undefined, 'Y-MM')).join(' - ');
    },
  },

  watch: {
    menu(val) {
      if (val) return;

      this.selectedMonth = cloneDeep(this.localValue);
    },

    modelValue(newValue) {
      this.selectedMonth = newValue;
    },
  },

  methods: {
    isSelected(month) {
      const monthToFind = `${this.currentYear}-${month.value}`;
      const [begin, end] = this.selectedMonth;

      if (!end) {
        return this.selectedMonth.includes(monthToFind);
      }

      return UtilsDate.isBetween(monthToFind, begin, end, 'Y-MM');
    },

    onSelectMonth(month) {
      const selectedDate = `${this.currentYear}-${month.value}`;

      if (!this.selectedMonth.length) {
        return this.selectedMonth.push(selectedDate);
      }

      if (this.selectedMonth.length > 1) {
        return (this.selectedMonth = [selectedDate]);
      }

      const [startMonth] = this.selectedMonth;

      if (UtilsDate.isBefore(selectedDate, startMonth, 'Y-MM')) {
        this.selectedMonth.unshift(selectedDate);
      } else {
        this.selectedMonth.push(selectedDate);
      }
    },

    onCancel() {
      this.menu = false;
      this.selectedMonth = cloneDeep(this.modelValue);
    },

    onConfirm() {
      let [start, end] = this.selectedMonth;
      end = end ?? start;

      this.localValue = [start, end];
      this.menu = false;
    },
    onClear() {
      this.localValue = [];
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/datepicker.scss';

.ag-month-picker {
  display: flex;
  flex-direction: column;
  background-color: white;
  box-shadow:
    0 5px 5px -3px rgba(0, 0, 0, 0.2),
    0 8px 10px 1px rgba(0, 0, 0, 0.14),
    0 3px 14px 2px rgba(0, 0, 0, 0.12);
  transition: background-color 0.25s ease;
  border-radius: 4px;
  font-weight: 400;
}

.ag-month-picker__header {
  min-height: 45px;
  padding: 4px 16px;
  align-items: center;
  display: flex;
  justify-content: space-between;
  position: relative;

  .ag-month-picker__value {
    text-align: center;
    font-size: $font-size-xxs;
    color: $neutral-color-high-medium !important;
    font-weight: 700;
  }
}

.ag-month-picker__body {
  padding: 0 8px;
  position: relative;
  display: grid;
  grid-template-columns: 86px 86px 86px;
  grid-template-rows: 36px 36px 36px 36px;
  row-gap: 8px;
  column-gap: 4px;

  button {
    font-size: 12px;
    color: $neutral-color-high-light;
    font-weight: 400;
    border-radius: 4px;

    &:hover {
      background-color: $brand-color-highlight-light;
    }

    &[data-selected='true'] {
      color: $neutral-color-low-light;
      background-color: $brand-color-primary-medium;
      border: $border-width-hairline $border-default $brand-color-primary-medium;
    }
  }
}

.ag-date-picker-range-month__actions {
  display: flex;
  gap: 8px;
  padding: 14px;
}
</style>
